/**
 * NewReviewsAlertWidget — dashboard tile that nudges the owner to reply
 * to fresh Google reviews within the first hour (small SEO ranking
 * boost). Sits beside CollectionEmailAuditWidget + ReviewFunnelWidget
 * in the admin dashboard header.
 *
 * Data source: GET /api/reputation/new-reviews (admin-gated). Backend
 * polls Google Places API every 15 min and stores new reviews in the
 * `google_reviews` Mongo collection. The Places API doesn't allow
 * posting replies from an app, so the "Reply now" button opens the
 * Google Business Profile console in a new tab (owner replies there).
 *
 * Tile face states:
 *   - Any review with age_hours < 1  →  red pulse "N urgent" badge
 *   - Any pending but no urgent      →  amber "N to reply" badge
 *   - No pending                     →  neutral "reviews clean" chip
 *   - Endpoint 401/503               →  widget hides silently
 *
 * Polls every 60s while mounted so the "urgent" window is enforced in
 * near real-time without spamming the backend.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Bell, ExternalLink, Loader2, RefreshCw, Star, Check, Link2 } from "lucide-react";
import { API } from "../../App";
import { useDashboardWidgetPanel } from "../../lib/useDashboardWidgetPanel";

const POLL_INTERVAL_MS = 60_000;

const NewReviewsAlertWidget = () => {
  const [data, setData] = useState(null);
  const [gbp, setGbp] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const { open, toggle, panelRef, tileRef } = useDashboardWidgetPanel("new-reviews");
  const [error, setError] = useState(null);
  const [dismissing, setDismissing] = useState({});

  const loadGbp = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reputation/gbp/status`);
      setGbp(res.data);
    } catch {
      setGbp(null); // non-admin or endpoint unavailable — hide the card
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/reputation/new-reviews`, {
        params: { window_hours: 168 },
      });
      setData(res.data);
    } catch (err) {
      const code = err.response?.status;
      if (code === 401 || code === 403 || code === 503) {
        // Non-admin OR endpoint disabled (no Google keys) → hide the
        // widget silently, matches CollectionEmailAuditWidget behaviour.
        setData({ __hidden: true });
      } else {
        setError(err.response?.data?.detail || err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadGbp();
    const t = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load, loadGbp]);

  // Surface the outcome of the OAuth round-trip. Google redirects back to
  // /dashboard?gbp=connected (or an error variant) via the backend
  // callback, so read it once and clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("gbp");
    if (!status) return;
    const detail = params.get("gbp_detail");
    const messages = {
      connected: "Google Business Profile connected — reviews syncing.",
      connected_pick_location: "Connected. Pick which location to sync below.",
      connected_sync_failed: "Connected, but the first sync failed. Try Sync now.",
      cancelled: "Google connection cancelled.",
      error: detail || "Google connection failed.",
    };
    setSyncMsg(messages[status] || status);
    loadGbp();
    load();
    params.delete("gbp");
    params.delete("gbp_detail");
    const qs = params.toString();
    window.history.replaceState(
      {}, "", window.location.pathname + (qs ? `?${qs}` : ""),
    );
  }, [load, loadGbp]);

  const connectGoogle = async () => {
    setConnecting(true);
    try {
      const res = await axios.post(`${API}/reputation/gbp/oauth/start`);
      window.location.href = res.data.authorize_url;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setConnecting(false);
    }
  };

  const dismiss = async (reviewId) => {
    setDismissing((d) => ({ ...d, [reviewId]: true }));
    try {
      await axios.post(`${API}/reputation/reviews/${reviewId}/dismiss`);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setDismissing((d) => {
        const { [reviewId]: _, ...rest } = d;
        return rest;
      });
    }
  };

  const forcePoll = async () => {
    setLoading(true);
    setSyncMsg(null);
    try {
      // Business Profile sync is the only review source now — the Places
      // poller was removed in June 2026.
      if (gbp?.connected) {
        const res = await axios.post(`${API}/reputation/gbp/sync-now`);
        if (res.data?.status === "already_running") {
          setSyncMsg("A sync is already running…");
        } else {
          setSyncMsg("Sync started — fetching every review, this can take a minute…");
        }
        // The sync runs in the background (a full first crawl can outlast
        // the gateway timeout), so poll status until it finishes.
        await pollUntilSyncDone();
      } else {
        setSyncMsg("Connect Google Business Profile first to sync reviews.");
      }
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const pollUntilSyncDone = async (attempts = 40) => {
    for (let i = 0; i < attempts; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 3000));
      let status;
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await axios.get(`${API}/reputation/gbp/status`);
        status = res.data;
        setGbp(status);
      } catch {
        return;
      }
      if (!status.sync_in_progress) {
        const s = status.last_sync_summary || {};
        setSyncMsg(
          status.last_sync_error
            ? `Sync failed: ${status.last_sync_error}`
            : `Synced ${s.reviews_returned ?? 0} review${s.reviews_returned === 1 ? "" : "s"} across ${s.pages_fetched ?? 0} page(s) — ${s.inserted ?? 0} new.`
        );
        await load();
        return;
      }
    }
    setSyncMsg("Sync still running — check back in a moment.");
  };

  const selectLocation = async (candidate) => {
    setLoading(true);
    try {
      await axios.post(`${API}/reputation/gbp/select-location`, {
        account_name: candidate.account_name,
        location_name: candidate.location_name,
      });
      setSyncMsg("Location selected — syncing reviews…");
      await pollUntilSyncDone();
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!data || data.__hidden) return null;

  const urgent = data.total_urgent ?? 0;
  const pending = data.total_pending ?? 0;
  const hasUrgent = urgent > 0;
  const hasPending = pending > 0;

  const tileClass = hasUrgent
    ? "bg-rose-500/15 border-rose-500/40 text-rose-300 animate-pulse"
    : hasPending
    ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
    : "bg-slate-800/50 border-slate-700 text-slate-400";

  const tileLabel = hasUrgent
    ? `${urgent} to reply now`
    : hasPending
    ? `${pending} pending`
    : "Reviews clean";

  return (
    <div className="relative" data-testid="new-reviews-alert-widget">
      <button
        ref={tileRef}
        type="button"
        onClick={toggle}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${tileClass}`}
        title={hasUrgent ? "New reviews — reply within the hour!" : "New Google reviews"}
        data-testid="new-reviews-tile-btn"
      >
        <Bell className="w-4 h-4" />
        <span data-testid="new-reviews-headline">{tileLabel}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-slate-950/80 backdrop-blur-sm sm:bg-transparent sm:backdrop-blur-none" aria-hidden />
          <div
            ref={panelRef}
            className="fixed left-2 right-2 top-24 max-w-md mx-auto z-50 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:mx-0 w-auto sm:w-[380px] bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-4 max-h-[70dvh] overflow-y-auto"
            data-testid="new-reviews-panel"
          >
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="text-white font-semibold text-sm">New Google reviews</h4>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Reply within an hour for a small ranking boost.
              </p>
            </div>
            <button
              type="button"
              onClick={forcePoll}
              disabled={loading}
              className="text-slate-400 hover:text-white p-1 disabled:opacity-50"
              title="Poll Google now"
              data-testid="new-reviews-poll-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
          </div>

          {error && <p className="text-rose-400 text-xs mb-2">Error: {error}</p>}
          {syncMsg && (
            <p className="text-emerald-300 text-xs mb-2" data-testid="gbp-sync-msg">{syncMsg}</p>
          )}

          {/* Google Business Profile connection card. Replaces the Places
              API poller, which only ever saw Google's relevance-ranked
              top-5 reviews. Connected = full paginated access. */}
          {gbp && (
            <div
              className="mb-3 rounded border border-slate-700 bg-slate-800/60 p-2.5"
              data-testid="gbp-connection-card"
            >
              {!gbp.configured ? (
                <p className="text-[11px] text-slate-400" data-testid="gbp-not-configured">
                  Google Business Profile not configured yet — awaiting OAuth client ID
                  and secret on the backend.
                </p>
              ) : gbp.needs_reconnect ? (
                <div className="space-y-2" data-testid="gbp-needs-reconnect">
                  <p className="text-[11px] text-rose-300">
                    Google access was revoked{gbp.revoked_reason ? ` (${gbp.revoked_reason})` : ""} —
                    reviews have stopped syncing.
                  </p>
                  <button
                    type="button"
                    onClick={connectGoogle}
                    disabled={connecting}
                    className="w-full text-[11px] py-1.5 px-2 rounded bg-rose-500/20 hover:bg-rose-500/30 text-rose-100 border border-rose-500/40 font-medium disabled:opacity-50"
                    data-testid="gbp-reconnect-btn"
                  >
                    {connecting ? "Redirecting…" : "Reconnect Google Business Profile"}
                  </button>
                </div>
              ) : !gbp.connected ? (
                <div className="space-y-2">
                  <p className="text-[11px] text-slate-400">
                    Connect your Business Profile to sync <strong>every</strong> review, not
                    just Google&apos;s top 5.
                  </p>
                  <button
                    type="button"
                    onClick={connectGoogle}
                    disabled={connecting}
                    className="w-full flex items-center justify-center gap-1.5 text-[11px] py-1.5 px-2 rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-100 border border-emerald-500/40 font-medium disabled:opacity-50"
                    data-testid="gbp-connect-btn"
                  >
                    <Link2 className="w-3 h-3" />
                    {connecting ? "Redirecting…" : "Connect Google Business Profile"}
                  </button>
                </div>
              ) : (
                <div className="space-y-1" data-testid="gbp-connected">
                  <div className="flex items-center gap-1.5 text-[11px] text-emerald-300">
                    <Check className="w-3 h-3" />
                    Connected
                    {gbp.location_title ? ` — ${gbp.location_title}` : ""}
                  </div>
                  <p className="text-[10px] text-slate-500" data-testid="gbp-sync-stats">
                    {gbp.stored_review_count ?? 0} review
                    {gbp.stored_review_count === 1 ? "" : "s"} stored
                    {gbp.total_review_count != null ? ` of ${gbp.total_review_count} on Google` : ""}
                    {gbp.average_rating != null ? ` · ${gbp.average_rating}★` : ""}
                    {gbp.last_sync_at
                      ? ` · last sync ${new Date(gbp.last_sync_at).toLocaleString()}`
                      : " · not synced yet"}
                  </p>
                  {gbp.last_sync_error && (
                    <p className="text-[10px] text-amber-400" data-testid="gbp-last-sync-error">
                      Last sync error: {gbp.last_sync_error}
                    </p>
                  )}
                  {gbp.sync_in_progress && (
                    <p className="text-[10px] text-sky-300" data-testid="gbp-sync-in-progress">
                      Sync running…
                    </p>
                  )}
                  {!gbp.location_name && (gbp.candidates || []).length > 0 && (
                    <div className="pt-1 space-y-1" data-testid="gbp-location-picker">
                      <p className="text-[10px] text-slate-400">Choose the location to sync:</p>
                      {gbp.candidates.map((c) => (
                        <button
                          key={`${c.account_name}-${c.location_name}`}
                          type="button"
                          onClick={() => selectLocation(c)}
                          className="w-full text-left text-[11px] py-1 px-2 rounded bg-slate-700 hover:bg-slate-600 text-slate-100"
                          data-testid={`gbp-location-option-${c.location_name}`}
                        >
                          {c.location_title || c.location_name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {(!data.reviews || data.reviews.length === 0) ? (
            <div className="py-8 text-center">
              <p className="text-slate-400 text-sm">No new reviews in the last 7 days.</p>
              <p className="text-slate-600 text-xs mt-2">
                {gbp?.connected
                  ? "Full review sync runs every 2 hours. Click refresh to sync now."
                  : "Connect Google Business Profile above to start syncing reviews."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.reviews.map((r) => (
                <ReviewCard
                  key={r.review_id}
                  review={r}
                  dismissing={dismissing[r.review_id]}
                  onDismiss={() => dismiss(r.review_id)}
                />
              ))}
            </div>
          )}

          <p className="text-[10px] text-slate-600 mt-3 leading-relaxed">
            {gbp?.connected || data.data_source === "google_business_profile"
              ? "Source: Google Business Profile API — every review, fetched page by page. Replying from inside the dashboard isn't wired up yet, so \u201cReply on Google\u201d opens your Business Profile console."
              : "No review source connected — connect Google Business Profile above to start syncing reviews."}
          </p>
        </div>
        </>
      )}
    </div>
  );
};

const ReviewCard = ({ review, dismissing, onDismiss }) => {
  const stars = Array.from({ length: 5 }, (_, i) => i < (review.rating || 0));
  const ageHours = review.age_hours ?? 0;
  const isUrgent = review.is_urgent;
  const isDismissed = !!review.dismissed_at;

  const ageLabel =
    ageHours < 1
      ? `${Math.round(ageHours * 60)} min ago`
      : ageHours < 24
      ? `${Math.round(ageHours)}h ago`
      : `${Math.round(ageHours / 24)}d ago`;

  return (
    <div
      className={`rounded border p-2.5 ${
        isUrgent
          ? "bg-rose-500/10 border-rose-500/40"
          : isDismissed
          ? "bg-slate-800/40 border-slate-700 opacity-60"
          : "bg-slate-800 border-slate-700"
      }`}
      data-testid={`new-review-card-${review.review_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-1">
            {stars.map((filled, i) => (
              <Star
                key={i}
                className={`w-3 h-3 ${filled ? "fill-amber-400 text-amber-400" : "text-slate-600"}`}
              />
            ))}
            <span className="text-[10px] text-slate-400 ml-1">{ageLabel}</span>
            {isUrgent && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-500/30 text-rose-200 font-semibold uppercase tracking-wide ml-auto">
                Reply now
              </span>
            )}
            {!isUrgent && review.is_new_since_last_sync && (
              <span
                className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/25 text-emerald-200 font-semibold uppercase tracking-wide ml-auto"
                data-testid={`new-review-new-badge-${review.review_id}`}
              >
                New
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-400 mb-1 truncate" title={review.author_name}>
            {review.author_name || "Anonymous"}
          </p>
          {review.text && (
            <p className="text-xs text-slate-300 line-clamp-3">{review.text}</p>
          )}
          {review.has_reply && (
            <p
              className="text-[10px] text-emerald-400/80 mt-1"
              data-testid={`new-review-replied-${review.review_id}`}
            >
              Replied on Google
            </p>
          )}
        </div>
      </div>

      <div className="flex gap-2 mt-2">
        <a
          href={review.reply_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-1 text-[11px] py-1.5 px-2 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/30 font-medium"
          data-testid={`new-review-reply-link-${review.review_id}`}
        >
          Reply on Google <ExternalLink className="w-3 h-3" />
        </a>
        {!isDismissed && (
          <button
            type="button"
            onClick={onDismiss}
            disabled={dismissing}
            className="flex items-center justify-center gap-1 text-[11px] py-1.5 px-2 rounded bg-slate-700 hover:bg-slate-600 text-slate-200 border border-slate-600 disabled:opacity-50"
            data-testid={`new-review-dismiss-btn-${review.review_id}`}
          >
            {dismissing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Handled
          </button>
        )}
      </div>
    </div>
  );
};

export default NewReviewsAlertWidget;
